import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, ArrowLeft, ArrowRight, Save, X, Edit3, Check } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { fmtMoney } from "@/lib/format";

/** STAS-only catalog of pricing items.
 *  Managers pick from this list when pricing a single application.
 */
export default function PricingCatalogPage() {
  const { user } = useAuth();
  const { theme, dir, lang } = useI18n();
  const navigate = useNavigate();
  const isRoot = !!user?.role?.is_root;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/pricing-catalog");
      setItems(data.items || []);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (isRoot) load(); }, [isRoot]);

  const T = lang === "ar" ? AR : EN;

  if (!isRoot) {
    return (
      <div dir={dir} style={{ padding: 32, color: theme.errorText, textAlign: "center" }}>
        {T.no_access}
      </div>
    );
  }

  const css = makeStyles(theme);
  const BackIcon = dir === "rtl" ? ArrowRight : ArrowLeft;

  const startEdit = (it) => {
    setEditingId(it.id);
    setEditDraft({ ...it });
  };

  const saveEdit = async () => {
    if (!editDraft) return;
    try {
      const { id, ...changes } = editDraft;
      await api.patch(`/admin/pricing-catalog/${id}`, changes);
      setEditingId(null); setEditDraft(null);
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const removeItem = async (id) => {
    if (!window.confirm(T.confirm_delete)) return;
    try {
      await api.delete(`/admin/pricing-catalog/${id}`);
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div dir={dir} style={css.page} data-testid="pricing-catalog-page">
      <header style={css.head}>
        <button onClick={() => navigate("/")} style={css.backBtn} data-testid="catalog-back">
          <BackIcon size={14} strokeWidth={2.4} /> {T.back}
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={css.title}>{T.title}</h1>
          <p style={css.sub}>{T.sub}</p>
        </div>
        <button onClick={() => setShowAdd(true)} style={css.btnGold} data-testid="catalog-add-open">
          <Plus size={16} strokeWidth={2.4} /> {T.add_new}
        </button>
      </header>

      {err && <div style={css.err}>{err}</div>}

      {loading ? (
        <div style={css.loading}>…</div>
      ) : (
        <div style={css.list}>
          <div style={css.listHead}>
            <span>{T.col_name}</span>
            <span>{T.col_price}</span>
            <span>{T.col_share}</span>
            <span>{T.col_revenue}</span>
            <span>{T.col_auto}</span>
            <span>{T.col_status}</span>
            <span></span>
          </div>
          {items.length === 0 ? (
            <div style={css.empty}>{T.empty}</div>
          ) : items.map((it) => (
            editingId === it.id ? (
              <EditRow
                key={it.id} draft={editDraft} setDraft={setEditDraft}
                theme={theme} T={T} onSave={saveEdit} onCancel={() => { setEditingId(null); setEditDraft(null); }}
              />
            ) : (
              <ViewRow
                key={it.id} item={it} theme={theme} T={T}
                onEdit={() => startEdit(it)} onDelete={() => removeItem(it.id)}
              />
            )
          ))}
        </div>
      )}

      {showAdd && (
        <AddModal
          theme={theme} T={T} onClose={() => setShowAdd(false)}
          onCreated={() => { setShowAdd(false); load(); }}
        />
      )}
    </div>
  );
}

function ViewRow({ item, theme, T, onEdit, onDelete }) {
  const css = makeStyles(theme);
  return (
    <div style={css.row} data-testid={`catalog-row-${item.code}`}>
      <span>
        <span style={{ fontWeight: 800 }}>{item.name_ar}</span>
        <span style={{ display: "block", fontSize: 11, color: theme.textMuted, fontFamily: "monospace", direction: "ltr" }}>{item.code}</span>
      </span>
      <span style={css.cellMoney}>{fmtMoney(item.default_price)}</span>
      <span style={css.cellMoney} title="حصة المكتب من السعر — الباقي يذهب لطرف خارجي (مترجم/معقّب/سفارة)">
        <span style={{ color: "#16a34a", fontWeight: 800 }}>{fmtMoney(item.office_share ?? item.default_price)}</span>
      </span>
      <span>
        {item.is_office_revenue ? (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            padding: "3px 9px", borderRadius: 999, fontSize: 10, fontWeight: 900,
            background: "rgba(22,163,74,0.14)", color: "#16a34a",
            border: "1.5px solid rgba(22,163,74,0.55)",
          }} title="دخل المكتب — يُحتسب ضمن البونص">● دخل المكتب</span>
        ) : (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            padding: "3px 9px", borderRadius: 999, fontSize: 10, fontWeight: 900,
            background: "rgba(234,88,12,0.14)", color: "#ea580c",
            border: "1.5px solid rgba(234,88,12,0.55)",
          }} title="غير ربحي — لا يدخل ضمن إيرادات المكتب">○ غير ربحي</span>
        )}
      </span>
      <span style={{ fontSize: 11, color: theme.textMuted }}>{item.auto_from_form_field || "—"}</span>
      <span style={css.statusPill(item.is_active)}>{item.is_active ? T.active : T.inactive}</span>
      <span style={{ display: "flex", gap: 4 }}>
        <button onClick={onEdit} style={css.iconBtn} data-testid={`catalog-edit-${item.code}`}>
          <Edit3 size={14} strokeWidth={2.4} />
        </button>
        <button onClick={onDelete} style={css.iconBtnDanger} data-testid={`catalog-delete-${item.code}`}>
          <Trash2 size={14} strokeWidth={2.4} />
        </button>
      </span>
    </div>
  );
}

function EditRow({ draft, setDraft, theme, T, onSave, onCancel }) {
  const css = makeStyles(theme);
  const u = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  return (
    <div style={css.rowEdit}>
      <input style={css.input} value={draft.name_ar} onChange={(e) => u("name_ar", e.target.value)} placeholder={T.col_name} />
      <input type="number" style={css.input} value={draft.default_price} onChange={(e) => u("default_price", Number(e.target.value))} placeholder="السعر الكامل" />
      <input type="number" style={css.input} value={draft.office_share ?? ""} onChange={(e) => u("office_share", e.target.value === "" ? null : Number(e.target.value))} placeholder="حصة المكتب" title="ما يبقى للمكتب من السعر (الباقي لطرف خارجي)" />
      <label style={css.toggle}>
        <input type="checkbox" checked={!!draft.is_office_revenue} onChange={(e) => u("is_office_revenue", e.target.checked)} />
        {T.col_revenue}
      </label>
      <span style={{ fontSize: 11, color: theme.textMuted }}>{draft.auto_from_form_field || "—"}</span>
      <label style={css.toggle}>
        <input type="checkbox" checked={!!draft.is_active} onChange={(e) => u("is_active", e.target.checked)} />
        {T.active}
      </label>
      <span style={{ display: "flex", gap: 4 }}>
        <button onClick={onSave} style={css.iconBtnGold} data-testid="catalog-save-edit">
          <Save size={14} strokeWidth={2.4} />
        </button>
        <button onClick={onCancel} style={css.iconBtn} data-testid="catalog-cancel-edit">
          <X size={14} strokeWidth={2.4} />
        </button>
      </span>
    </div>
  );
}

function AddModal({ theme, T, onClose, onCreated }) {
  const css = makeStyles(theme);
  const [form, setForm] = useState({
    code: "", name_ar: "", name_en: "", default_price: 0,
    office_share: null,
    is_office_revenue: true, auto_from_form_field: null,
    is_active: true, display_order: 99,
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const u = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      await api.post("/admin/pricing-catalog", form);
      onCreated();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div style={css.modalBg} onClick={onClose}>
      <div style={css.modalCard} onClick={(e) => e.stopPropagation()} data-testid="catalog-add-modal">
        <h2 style={css.modalTitle}>{T.add_new}</h2>
        <div style={css.modalGrid}>
          <Field label={T.f_code} hint={T.hint_code}>
            <input style={css.input} value={form.code} onChange={(e) => u("code", e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))} placeholder="bank_translation" dir="ltr" data-testid="catalog-add-code" />
          </Field>
          <Field label={T.f_name_ar}>
            <input style={css.input} value={form.name_ar} onChange={(e) => u("name_ar", e.target.value)} placeholder="ترجمة كشف بنكي" data-testid="catalog-add-name-ar" />
          </Field>
          <Field label={T.f_name_en}>
            <input style={css.input} value={form.name_en} onChange={(e) => u("name_en", e.target.value)} placeholder="Bank statement translation" dir="ltr" />
          </Field>
          <Field label={T.f_price}>
            <input type="number" style={css.input} value={form.default_price} onChange={(e) => u("default_price", Number(e.target.value))} data-testid="catalog-add-price" />
          </Field>
          <Field label={T.f_office_share} hint={T.hint_office_share}>
            <input type="number" style={css.input}
              value={form.office_share ?? ""}
              onChange={(e) => u("office_share", e.target.value === "" ? null : Number(e.target.value))}
              placeholder={String(form.default_price || 0)}
              data-testid="catalog-add-share" />
          </Field>
          <Field label={T.f_order}>
            <input type="number" style={css.input} value={form.display_order} onChange={(e) => u("display_order", Number(e.target.value))} />
          </Field>
          <label style={css.toggle}>
            <input type="checkbox" checked={form.is_office_revenue} onChange={(e) => u("is_office_revenue", e.target.checked)} data-testid="catalog-add-revenue" />
            {T.f_revenue}
          </label>
        </div>
        {err && <div style={css.err}>{err}</div>}
        <div style={css.modalActions}>
          <button onClick={onClose} style={css.btnGhost}>{T.cancel}</button>
          <button onClick={submit} disabled={busy || !form.code || !form.name_ar} style={css.btnGold} data-testid="catalog-add-submit">
            <Plus size={14} strokeWidth={2.4} /> {T.create}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 11, opacity: 0.8, marginBottom: 4, fontWeight: 700 }}>{label}</div>
      {children}
      {hint && <div style={{ fontSize: 10, opacity: 0.55, marginTop: 3 }}>{hint}</div>}
    </label>
  );
}

const AR = {
  title: "كتالوج بنود التسعير",
  sub: "يدير STAS — تنعكس التغييرات فوراً للمدراء",
  back: "رجوع",
  add_new: "إضافة بند جديد",
  col_name: "الاسم",
  col_price: "السعر للعميل",
  col_share: "حصة المكتب",
  col_revenue: "دخل مكتب؟",
  col_auto: "تلقائي من النموذج",
  col_status: "الحالة",
  active: "مفعّل",
  inactive: "موقوف",
  empty: "لا توجد بنود — اضغط \"إضافة بند جديد\"",
  confirm_delete: "إيقاف هذا البند؟ لن يظهر للمدراء بعد ذلك.",
  no_access: "هذه الصفحة لمستخدم STAS فقط",
  f_code: "الكود الفني (إنجليزي)",
  hint_code: "أحرف صغيرة وأرقام و _ فقط",
  f_name_ar: "الاسم (عربي)",
  f_name_en: "الاسم (إنجليزي)",
  f_price: "السعر للعميل",
  f_office_share: "حصة المكتب",
  hint_office_share: "ما يبقى للمكتب من السعر — الباقي يذهب لطرف خارجي. اتركه فارغاً ليساوي السعر الكامل.",
  f_order: "ترتيب العرض",
  f_revenue: "هذا البند يُحتسب ضمن دخل المكتب",
  cancel: "إلغاء",
  create: "إضافة",
};

const EN = {
  title: "Pricing Catalog",
  sub: "Managed by STAS — changes propagate to managers instantly",
  back: "Back",
  add_new: "Add item",
  col_name: "Name",
  col_price: "Client price",
  col_share: "Office share",
  col_revenue: "Office revenue?",
  col_auto: "Auto from form",
  col_status: "Status",
  active: "Active",
  inactive: "Inactive",
  empty: "No items yet — click \"Add item\"",
  confirm_delete: "Deactivate this item? Managers will no longer see it.",
  no_access: "STAS-only page",
  f_code: "Technical code (English)",
  hint_code: "lowercase letters, numbers, and underscore only",
  f_name_ar: "Name (Arabic)",
  f_name_en: "Name (English)",
  f_price: "Client price",
  f_office_share: "Office share",
  hint_office_share: "Office's net share. Rest goes to third party. Leave blank to keep full price.",
  f_order: "Display order",
  f_revenue: "Counts as office revenue",
  cancel: "Cancel",
  create: "Add",
};

const makeStyles = (theme) => ({
  page: {
    minHeight: "100vh", background: theme.bg, color: theme.text,
    fontFamily: "'Tajawal','Segoe UI',sans-serif",
    padding: "20px 16px", maxWidth: 1200, margin: "0 auto",
  },
  head: { display: "flex", alignItems: "center", gap: 12, marginBottom: 18, flexWrap: "wrap" },
  backBtn: {
    display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px",
    background: "transparent", border: `1px solid ${theme.border}`,
    color: theme.text, borderRadius: 999, cursor: "pointer", fontSize: 12, fontWeight: 700,
    fontFamily: "inherit",
  },
  title: { color: theme.gold, fontSize: 22, fontWeight: 900, margin: 0 },
  sub: { fontSize: 12, color: theme.textMuted, margin: "4px 0 0" },
  btnGold: {
    padding: "10px 16px", background: theme.goldGradient,
    color: theme.name === "dark" ? "#0a0a0a" : "#fff",
    border: "none", borderRadius: 10, cursor: "pointer",
    display: "inline-flex", alignItems: "center", gap: 6,
    fontSize: 13, fontWeight: 900, fontFamily: "inherit",
  },
  btnGhost: {
    padding: "10px 16px", background: "transparent", color: theme.text,
    border: `1px solid ${theme.border}`, borderRadius: 10, cursor: "pointer",
    fontSize: 13, fontWeight: 700, fontFamily: "inherit",
  },
  err: { padding: 12, background: theme.errorBg, color: theme.errorText, border: `1px solid ${theme.errorBorder}`, borderRadius: 10, marginBottom: 12, fontSize: 13 },
  loading: { padding: 40, textAlign: "center", color: theme.textMuted },
  empty: { padding: 40, textAlign: "center", color: theme.textMuted },
  list: { background: theme.surfaceCard, border: `1px solid ${theme.borderSoft}`, borderRadius: 12, overflow: "hidden" },
  listHead: {
    display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr auto",
    gap: 10, padding: "12px 16px",
    background: theme.surfaceAlt,
    borderBottom: `1px solid ${theme.borderSoft}`,
    fontSize: 11, fontWeight: 800, color: theme.textMuted, letterSpacing: 0.3,
  },
  row: {
    display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr auto",
    gap: 10, padding: "12px 16px",
    borderBottom: `1px solid ${theme.borderSoft}`,
    alignItems: "center", fontSize: 13,
  },
  rowEdit: {
    display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr auto",
    gap: 10, padding: "12px 16px",
    borderBottom: `1px solid ${theme.borderSoft}`,
    alignItems: "center", fontSize: 13,
    background: theme.surfaceAlt,
  },
  cellMoney: { fontFamily: "monospace", fontWeight: 800, color: theme.gold, direction: "ltr" },
  statusPill: (active) => ({
    fontSize: 10, fontWeight: 800, padding: "3px 10px", borderRadius: 999,
    background: active ? "rgba(22,163,74,0.15)" : theme.surfaceAlt,
    color: active ? "#16a34a" : theme.textMuted,
    display: "inline-block", justifySelf: "start",
  }),
  iconBtn: {
    background: "transparent", border: `1px solid ${theme.border}`,
    color: theme.text, padding: 6, borderRadius: 8, cursor: "pointer",
    display: "inline-flex", alignItems: "center",
  },
  iconBtnGold: {
    background: theme.goldGradient, border: "none",
    color: theme.name === "dark" ? "#0a0a0a" : "#fff",
    padding: 6, borderRadius: 8, cursor: "pointer",
    display: "inline-flex", alignItems: "center",
  },
  iconBtnDanger: {
    background: "transparent", border: "1px solid #dc2626",
    color: "#dc2626", padding: 6, borderRadius: 8, cursor: "pointer",
    display: "inline-flex", alignItems: "center",
  },
  input: {
    width: "100%", padding: "8px 10px", borderRadius: 8,
    background: theme.inputBg, border: `1px solid ${theme.inputBorder}`,
    color: theme.text, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box",
  },
  toggle: {
    display: "inline-flex", alignItems: "center", gap: 6,
    fontSize: 12, fontWeight: 700, cursor: "pointer",
  },
  modalBg: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 12,
  },
  modalCard: {
    background: theme.surface, border: `1px solid ${theme.borderAccent}`,
    borderRadius: 16, padding: 24, width: "100%", maxWidth: 560,
    color: theme.text,
  },
  modalTitle: { color: theme.gold, fontSize: 18, fontWeight: 900, marginTop: 0 },
  modalGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginBottom: 16 },
  modalActions: { display: "flex", gap: 10, justifyContent: "flex-end" },
});
